import { Dispatch } from '@reduxjs/toolkit';
import {
	setSuggestionsProductNotifierExpiration,
	clearSuggestionsProductNotifierExpiration,
	setCatalogMenuItems,
	setCatalogLoading,
	setLoadingPromotionDetails,
	setIsPromotionActive,
	setIncentiveCampaignFlags,
	setIncentiveCurrentCampaignData,
	setIncentiveCurrentCampaignLoading,
	setIncentiveCurrentCampaignFetched,
} from './actions';
import dayjs from 'dayjs';
import { CatalogMenuItemsParams, CatalogReducer } from './interfaces';
import { menuItemsData } from './helpers';
import { getCurrentCampaign } from 'services/currentCampaign';
import { getMenuFlags } from 'services/products';
import { PosReducer } from 'stores/pos/interfaces';
import { getIncentiveCampaignsFlags } from 'services/campaigns';
import to from 'await-to-js';

export const handleCatalogLoading = (loading: CatalogReducer['loading']): any => {
	return (dispatch: Dispatch) => {
		dispatch(setCatalogLoading(loading));
	};
};

export const handleSetIsPromotionActive = (loading: CatalogReducer['isPromotionActive']): any => {
	return (dispatch: Dispatch) => {
		dispatch(setIsPromotionActive(loading));
	};
};

const incentiveCampaignRequests = new Map<
	PosReducer['selectedPos']['id'],
	Promise<CatalogReducer['incentiveCampaignFlags']>
>();

export const handleSetIncentiveCampaign = (posId: PosReducer['selectedPos']['id']): any => {
	return async (dispatch: Dispatch) => {
		const currentRequest = incentiveCampaignRequests.get(posId);

		if (currentRequest) return currentRequest;

		dispatch(setIncentiveCurrentCampaignFetched(false));
		dispatch(setIncentiveCurrentCampaignData(undefined));

		const request = (async () => {
			const [_, incentiveCampaignFlags] = await to(getIncentiveCampaignsFlags({ posId }));

			dispatch(setIncentiveCampaignFlags(incentiveCampaignFlags));

			if (!incentiveCampaignFlags?.hasCurrentCampaign) {
				dispatch(setIncentiveCurrentCampaignLoading(false));
				dispatch(setIncentiveCurrentCampaignFetched(true));

				return incentiveCampaignFlags;
			}

			dispatch(setIncentiveCurrentCampaignLoading(true));

			const [__, currentCampaign] = await to(getCurrentCampaign({ posId }));

			dispatch(setIncentiveCurrentCampaignData(currentCampaign));
			dispatch(setIncentiveCurrentCampaignFetched(true));
			dispatch(setIncentiveCurrentCampaignLoading(false));

			return incentiveCampaignFlags;
		})();

		incentiveCampaignRequests.set(posId, request);

		try {
			return await request;
		} finally {
			incentiveCampaignRequests.delete(posId);
		}
	};
};

export const handleSetCatalogMenuItems = (selectedPos: PosReducer['selectedPos']): any => {
	return async (dispatch: Dispatch) => {
		try {
			dispatch(handleCatalogLoading(true));

			const menuFlags = await getMenuFlags(selectedPos.id);

			const catalogMenuItems: CatalogMenuItemsParams[] = [];

			dispatch(handleSetIsPromotionActive(menuFlags.hasPromotions));

			const incentiveCampaignFlags = await dispatch(handleSetIncentiveCampaign(selectedPos.id));

			catalogMenuItems.push({
				...menuItemsData.promotions,
				status: menuFlags.hasPromotions ? 'AVAILABLE' : 'UNAVAILABLE',
			});

			catalogMenuItems.push({
				...menuItemsData.brands,
				status: 'AVAILABLE',
			});

			catalogMenuItems.push({
				...menuItemsData.suggestionsProduct,
				status: menuFlags.hasProductSuggestions && menuFlags.productSuggestionQuantity ? 'AVAILABLE' : 'UNAVAILABLE',
				counter: menuFlags.productSuggestionQuantity,
			});

			catalogMenuItems.push({
				...menuItemsData.releases,
				status: menuFlags.hasReleaseProducts ? 'AVAILABLE' : 'UNAVAILABLE',
			});

			const shouldShowIncentiveLink =
				incentiveCampaignFlags?.hasCurrentCampaign || incentiveCampaignFlags?.hasCampaignHistory;

			if (shouldShowIncentiveLink) {
				catalogMenuItems.push({
					...menuItemsData.incentiveCampaign,
					status: 'AVAILABLE',
				});
			}

			return dispatch(setCatalogMenuItems(catalogMenuItems));
		} catch {
			return dispatch(setCatalogMenuItems([]));
		} finally {
			dispatch(handleCatalogLoading(false));
		}
	};
};

export const handleClearSuggestionsProductNotifierExpiration = (): any => {
	return (dispatch: Dispatch) => {
		dispatch(clearSuggestionsProductNotifierExpiration());
	};
};

export const handleSetSuggestionsProductNotifierExpiration = (): any => {
	return (dispatch: Dispatch, getState: any) => {
		const date = dayjs().add(5, 'days');

		dispatch(
			setSuggestionsProductNotifierExpiration({
				posId: getState().posReducer.selectedPos.id,
				notifierExpiration: date.format(),
			}),
		);
	};
};

export const handleLoadingPromotionDetails = (loading: CatalogReducer['promotionDetails']['loading']): any => {
	return (dispatch: Dispatch) => {
		dispatch(setLoadingPromotionDetails(loading));
	};
};
